import React from 'react';

type ExperienceItem = {
  company: string;
  role: string;
  location: string;
  dates: string;
  bullets: string[];
};

const experience: ExperienceItem[] = [
  {
    company: 'PGA TOUR',
    role: 'Senior Software Developer',
    location: 'Remote — Ponte Vedra Beach, FL',
    dates: 'Dec 2022 – Present',
    bullets: [
      'Technical lead and sole architect of a mission-critical broadcast data platform delivering real-time golf data to NBC, CBS, and PGA TOUR Studios during live broadcasts viewed by millions.',
      'Led and mentored a team of six external engineers, providing architectural direction and ensuring consistency across all integrations and services.',
      'Designed and built an event-driven, distributed architecture using .NET 8/10 (C#), AWS (ECS, Lambda, SQS, DynamoDB, S3, AppSync, ElastiCache), and React/TypeScript to process and transform real-time shot-tracking data into broadcast graphics and analytics.',
      'Implemented sub-second real-time data delivery via WebSocket services powering broadcast graphics and internal production tools.',
      'Contributed directly to the ShotLink 2.0 core platform—building Lambda functions, data routing services, and repository patterns that power the real-time scoring engine processing thousands of events per tournament.',
      'Built rapid prototyping pipelines enabling new broadcast features to move from concept to production in days instead of months.',
      'Implemented AI-assisted development workflows (Claude + Cursor) to automate ticket refinement and accelerate engineering throughput.',
      'Designed CI/CD pipelines with GitHub Actions supporting automated testing and deployments across 6+ AWS environments.',
    ],
  },
  {
    company: 'Gemini Sports Analytics',
    role: 'Senior Software Developer',
    location: 'Remote — Miami, FL',
    dates: 'May 2022 – Dec 2022',
    bullets: [
      'Co-founded the engineering team of a seed-stage startup, architecting the backend, database, and deployment layers of a cloud-based automated machine learning platform.',
      'Built a .NET Core 6 microservice architecture with PostgreSQL on AWS, utilizing S3, Lambdas, State Machines, EventBridge, and CloudWatch for efficient API performance and process logging.',
      'Implemented and maintained C# Lambda ETL processes to communicate with third-party APIs on scheduled workflows.',
      'Collaborated with external and internal design/frontend teams to plan and deliver Version 1.0, currently used by pilot customers.',
      'Hired, mentored, and reviewed code from junior developers while establishing consistent coding standards across the team.',
      'Configured GitHub Actions templates to automate unit testing and CI/CD for all repositories.',
      'Spearheaded documentation efforts by creating templates for API documentation, user guides, release notes, and FAQs.',
      'Served as a core member of the leadership team, contributing to product strategy, customer engagement, and hiring decisions.',
      'Participated in the LEAD Startup Accelerator program, collaborating with industry experts to further product development and business growth.',
    ],
  },
  {
    company: 'Par Technology',
    role: 'Software Engineer',
    location: 'Remote — San Diego, CA',
    dates: 'Jul 2021 – May 2022',
    bullets: [
      'Full-stack engineer on the Brink Point-of-Sale configuration, administration, and reporting web application used in 10,000+ restaurants including Arby’s, Dairy Queen, and Five Guys.',
      'Designed and maintained features across Angular/WCF (.NET MVC), .NET/.NET Core monolith/microservices, and SQL Server/PostgreSQL.',
      'Contributed to a company-wide monolith-to-microservice migration, redesigning legacy SOAP APIs into modern RESTful interfaces to reduce overhead and improve response times.',
      'Developed PowerShell scripts to automate developer machine configuration in both Azure (ARMS) and non-virtual environments.',
      'Participated in the 2022 company-wide Mentorship program, learning from the Director of Enterprise Sales about customer relations, business strategy, and sales-development team dynamics.',
    ],
  },
  {
    company: 'Houston Astros',
    role: 'Full-Stack Developer',
    location: 'Houston, TX',
    dates: 'Dec 2019 – Jul 2021',
    bullets: [
      'One of five developers on the R&D team responsible for all data ingestion, storage, and web applications powering the Baseball Analytics and Baseball Operations front office.',
      'Developed and maintained C# .NET Core REST APIs deployed to Azure App Services for internal web applications.',
      'Built and reviewed React JS code while maintaining a legacy .NET MVC application (CSHTML/C#/SQL).',
      'Created C#/Python console applications for nightly ETL processes pulling data from third-party sources into SQL Server and Azure Cosmos.',
      'Managed database tables, performed data maintenance, and wrote/debugged queries for analysts and leadership.',
      'Reviewed 100+ resumes and conducted phone screenings and interviews with potential candidates.',
    ],
  },
];

const Experience: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-900/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-300">
        Experience
        <span className="h-1 w-6 rounded-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-indigo-400" />
      </div>
      <div className="space-y-6">
        {experience.map((item) => (
          <article
            key={`${item.company}-${item.role}`}
            className="rounded-2xl border border-slate-800/80 bg-slate-900/60 p-5 text-sm text-slate-200 shadow-sm"
          >
            <div className="flex flex-col justify-between gap-2 md:flex-row md:items-baseline">
              <div>
                <h3 className="text-base font-semibold text-slate-50">
                  {item.role}
                  <span className="text-slate-400"> · {item.company}</span>
                </h3>
                <p className="text-xs text-slate-400">{item.location}</p>
              </div>
              <p className="text-xs font-medium text-slate-300">{item.dates}</p>
            </div>
            <ul className="mt-3 space-y-2 text-[13px] leading-relaxed">
              {item.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-2">
                  <span className="mt-[6px] h-1 w-1 flex-shrink-0 rounded-full bg-emerald-400" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </div>
  );
};

export default Experience;

